import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  FormControlLabel, 
  Switch, 
  Typography,
  InputAdornment,
  IconButton,
  Stack
} from '@mui/material';
import { ColorLens as ColorIcon } from '@mui/icons-material';
import { AccountDto } from '../../types/homeExpenses';

interface AccountFormProps {
  initial?: AccountDto;
  onSubmit: (data: {
    name: string;
    description?: string;
    color?: string;
    isPrimary: boolean;
  }) => void;
  onCancel?: () => void;
  loading?: boolean;
}

// Common colors for accounts
const PRESET_COLORS = [
  '#1f77b4', // Blue - for bank accounts
  '#ff7f0e', // Orange - for credit cards
  '#2ca02c', // Green - for savings
  '#d62728', // Red - for debt/loans
  '#9467bd', // Purple - for investments
  '#8c564b', // Brown - for cash
  '#e377c2', // Pink - for special accounts
  '#7f7f7f', // Gray - for other
  '#bcbd22', // Olive - for business
  '#17becf', // Cyan - for foreign accounts
  '#aec7e8', // Light Blue
  '#ffbb78', // Light Orange
  '#98df8a', // Light Green
  '#ff9896', // Light Red
  '#c5b0d5', // Light Purple
  '#c49c94', // Light Brown
  '#f7b6d3', // Light Pink
  '#c7c7c7', // Light Gray
];

const AccountForm: React.FC<AccountFormProps> = ({ 
  initial, 
  onSubmit, 
  onCancel, 
  loading = false 
}) => {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [color, setColor] = useState(initial?.color || PRESET_COLORS[0]);
  const [isPrimary, setIsPrimary] = useState(initial?.isPrimary || false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      color: color || undefined,
      isPrimary
    });
  };

  const handleColorSelect = (selectedColor: string) => {
    setColor(selectedColor);
    setShowColorPicker(false);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Stack spacing={3}>
        <TextField
          label="Nome da conta"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
          disabled={loading}
          placeholder="ex.: Conta bancária, Cartão de crédito, Dinheiro"
        />

        <TextField
          label="Descrição"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          disabled={loading}
          multiline
          rows={2}
          placeholder="Descrição ou notas opcionais sobre esta conta"
        />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Cor da conta
          </Typography>
          <TextField
            value={color}
            onChange={(e) => setColor(e.target.value)}
            fullWidth
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: color,
                      border: '1px solid #ccc'
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    size="small"
                  >
                    <ColorIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          {showColorPicker && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Escolha uma cor predefinida:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {PRESET_COLORS.map((presetColor) => (
                  <IconButton
                    key={presetColor}
                    onClick={() => handleColorSelect(presetColor)}
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: presetColor,
                      border: color === presetColor ? '2px solid #000' : '1px solid #ccc',
                      '&:hover': {
                        backgroundColor: presetColor,
                        opacity: 0.8
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              disabled={loading}
            />
          }
          label={
            <Box>
              <Typography variant="body2">Conta por defeito</Typography>
              <Typography variant="caption" color="text.secondary">
                A sua conta por defeito (só para si, cada utilizador escolhe a sua). Marcar esta substitui a anterior.
              </Typography>
            </Box>
          }
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          {onCancel && (
            <Button 
              onClick={onCancel} 
              disabled={loading}
              variant="outlined"
            >
              Cancelar
            </Button>
          )}
          <Button 
            type="submit" 
            variant="contained"
            disabled={loading || !name.trim()}
          >
            {loading ? 'A guardar...' : (initial ? 'Atualizar conta' : 'Criar conta')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default AccountForm;
